import Blockquote from "@tiptap/extension-blockquote";
import Bold from "@tiptap/extension-bold";
import BulletList from "@tiptap/extension-bullet-list";
import CharacterCount from "@tiptap/extension-character-count";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import Document from "@tiptap/extension-document";
import HardBreak from "@tiptap/extension-hard-break";
import Highlight from "@tiptap/extension-highlight";
import History from "@tiptap/extension-history";
import Italic from "@tiptap/extension-italic";
import OrderedList from "@tiptap/extension-ordered-list";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import Strike from "@tiptap/extension-strike";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import Text from "@tiptap/extension-text";
import { lowlight } from "lowlight/lib/core";
import { isMobile } from "react-device-detect";
import { InlineEmojiExtension } from "@circle-react/components/shared/uikit/TipTapBlockEditor/InlineEmojiExtension";
import { DesktopKeymap } from "@circle-react-shared/uikit/TipTap/Extensions/CustomKeymaps/Desktop";
import { MobileKeymap } from "@circle-react-shared/uikit/TipTap/Extensions/CustomKeymaps/Mobile";
import { CustomizedUnderline } from "@circle-react-shared/uikit/TipTap/Extensions/CustomizedUnderline";
import { CustomizedListItem } from "@circle-react-shared/uikit/TipTapBlockEditor/CustomizedListItemExtension";
import { DEFAULT_VARIABLES } from "@circle-react-uikit/InsertVariableButton";
import { ChatMessageLinkExtension } from "./CustomExtensions/ChatMessageLink";
import { CustomizedLink } from "./CustomExtensions/CustomizedLink";
import { EditedLabelExtension } from "./CustomExtensions/EditedLabel";
import { EmojiReplacer } from "./CustomExtensions/EmojiReplacer";
import { EntitiesExtension } from "./CustomExtensions/Entities";
import { MentionsExtension } from "./CustomExtensions/Mentions";

import(
  /* webpackChunkName: "HighlightJs_CommonLanguages" */ "highlight.js/lib/common"
)
  .then(module => {
    module.default.highlightAll();
  })
  .catch(console.error);

export interface TiptapExtensionProps {
  frameDocument?: Document | null;
  maxLength?: null | number;
  menubarProps: any;
  placeholder: string;
  portalTargetId?: string | null;
  shouldDisableEntities: boolean;
  shouldDisableMentions: boolean;
  shouldSupportCodeBlocks: boolean;
  shouldSupportTables: boolean;
  type: "workflow" | "chat";
}

export const getTiptapExtensions = ({
  placeholder,
  maxLength = null,
  menubarProps,
  type,
  shouldDisableEntities = false,
  shouldDisableMentions = false,
  portalTargetId = null,
  shouldSupportCodeBlocks = false,
  shouldSupportTables = false,
  frameDocument = null,
}: TiptapExtensionProps): any => {
  const isWorkflowType = type == "workflow";

  return [
    ...(shouldSupportTables
      ? [
          Table.configure({ resizable: false }),
          TableCell,
          TableHeader,
          TableRow,
        ]
      : []),
    ...(shouldSupportCodeBlocks
      ? [
          CodeBlockLowlight.configure({
            lowlight,
            HTMLAttributes: {
              spellCheck: "false",
            },
          }),
        ]
      : []),
    Document,
    Paragraph,
    Text,
    Bold,
    Blockquote,
    Italic,
    Strike,
    Highlight.configure({
      HTMLAttributes: {
        class: "highlight-class",
      },
    }),
    CustomizedUnderline,
    EditedLabelExtension,
    History,
    isMobile ? MobileKeymap : DesktopKeymap,
    CustomizedLink.configure({
      openOnClick: false,
      autolink: true,
      validate: (href: any) => {
        const dynamicVariableLabels: any = (
          menubarProps.dynamicVariables || DEFAULT_VARIABLES
        ).map(({ label }: any) => label.replace(/{{|}}/g, ""));
        return !dynamicVariableLabels.some((variable: any) =>
          variable.includes(href),
        );
      },
    }),
    Placeholder.configure({
      placeholder,
      emptyEditorClass: "is-editor-empty",
    }),
    {
      ...(!isWorkflowType &&
        !shouldDisableMentions &&
        MentionsExtension.configure({
          portalTargetId,
          frameDocument,
        })),
    },
    {
      ...(!shouldDisableEntities &&
        EntitiesExtension.configure({
          portalTargetId,
          frameDocument,
        })),
    },
    {
      ...(!shouldDisableEntities && ChatMessageLinkExtension),
    },
    HardBreak.extend({
      addKeyboardShortcuts() {
        // Disable all keyboard shortcuts for hard break
        return {};
      },
    }),
    EmojiReplacer,
    CharacterCount.configure({
      limit: maxLength,
    }),
    BulletList,
    OrderedList,
    CustomizedListItem.configure({ type }),
    InlineEmojiExtension.configure({
      portalTargetId,
      frameDocument,
    }),
  ];
};
